import Head from 'next/head';
import { useEffect } from 'react';
import { orderBy } from 'lodash';
import ArticleCard from '../components/articleCard';
import Avatar from '../components/avatar';
import Link from '../components/link';
import MetaLinks from '../components/metaLinks';
import MetaSocial from '../components/metaSocial';
import ApiError from '../error/ApiError';
import { useTranslation } from '../i18n';
import { Router } from '../routes';
import { encodeHtml, getBasePath, getUniqUsers, groupBy, sortBy } from '../utils';
import fetch from '../utils/fetch';
import { usePinnedArticleIds } from '../utils/articleUtils';

const i18nNs = ['common'];

function Collection({ query, desk, collection }) {
  const { t } = useTranslation(i18nNs);
  const articles =
    collection?.articles.filter(a => a.article?.user && !a.article.isDraft).map(a => a.article) ||
    [];
  const users = getUniqUsers(articles.map(article => article?.user).filter(u => !!u));
  let groupedArticles = groupBy(sortBy(collection?.articles, 'order'), a => a.section || 0);

  const writtenByText = t(`writtenByLabel${Math.min(users.length, 4)}`, {
    user1: encodeHtml(users[0]?.name),
    user2: encodeHtml(users[1]?.name),
    count: users.length,
  });

  useEffect(() => {
    if (query.slug !== collection.slug) {
      Router.replaceRoute(`${getBasePath(query)}/collections/${collection._id}-${collection.slug}`);
    }
  }, [collection._id]);

  const [pinnedArticleIds] = usePinnedArticleIds();
  const pinnedArticles = articles.filter(a => pinnedArticleIds.includes(a._id));
  // add pinned articles to the top of the list in a separate section
  if (pinnedArticles.length) {
    groupedArticles = {
      [-1]: pinnedArticles.map(article => ({ article })),
      ...groupedArticles,
    };
  }

  return (
    <div className="root">
      <Head>
        <title key="title">
          {collection.title} | {desk.languageFields.title}
        </title>
      </Head>
      <MetaLinks
        path={`/collections/${collection._id}-${collection.slug}`}
        language={collection.language}
        alternates={false}
      />
      <MetaSocial
        title={collection.title}
        description={collection.spoiler}
        type="website"
        image={desk.socialMediaImgUrl}
      />
      <div className="content">
        <div className="breadcrumbs lightText">
          <span className="darkText hoverable">
            <Link route="home">
              <a>{t('allCollections')}</a>
            </Link>
          </span>
          <i className="fas fa-angle-right angle" />
          <span>{collection.title}</span>
        </div>

        <div className="container collectionContainer">
          <div className="top">
            <i className={`icon ${collection.icon}`} />
            <div className="info">
              <h1>{collection.title}</h1>
              <p>{collection.spoiler}</p>
              <div className="bottom">
                {users.slice(0, 2).map(user => (
                  <Avatar bgColor="#ebeef1" user={user} key={user.name} />
                ))}
                {users.length > 2 && <Avatar bgColor="#ebeef1" text={`+${users.length - 2}`} />}
                <div className="bottomRight lightText">
                  <div className="darkText">
                    {t('numArticlesInCollection', { count: articles.length })}
                  </div>
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: writtenByText }} />
                </div>
              </div>
            </div>
          </div>
          {articles.length === 0 && <h2 className="noResults">{t('noArticlesInCollection')}</h2>}
          {orderBy(Object.entries(groupedArticles), e => e[0])
            .map(([sectionIdx, sectionArticles], idx) =>
            (!sectionArticles.length ? null : (
              <div className="articles" key={idx}>
                {Number(sectionIdx) > 0 && <h2>{collection.sections[Number(sectionIdx) - 1]}</h2>}
                {Number(sectionIdx) === -1 && <h2>{t('pinnedArticles')}</h2>}
                {sectionArticles
                  .filter(a => !!a.article && typeof a.article === 'object')
                  .map(a => (
                    <ArticleCard
                      {...a.article}
                      key={a.article._id}
                      isPinned={pinnedArticleIds.includes(a.article._id)}
                    />
                    ))}
              </div>
            )))}
        </div>
      </div>

      <style jsx>
        {`
          .root {
            margin-top: -20px;
          }
          .breadcrumbs {
            display: block;
            word-break: break-word;
            font-size: 14px;
          }
          .angle {
            margin: 0 5px;
          }
          .container {
            background: #ebeef1;
            margin-top: 15px;
            padding: 30px;
          }
          .top {
            position: relative;
            color: #666;
            padding: 0 20px 20px 150px;
          }
          .top .icon {
            font-size: 48px;
            position: absolute;
            left: 70px;
            top: 45%;
            transform: translate(-50%, -50%);
          }
          .top .info h1 {
            color: #212121;
            margin: 0 0 3px;
            font-size: 32px;
            font-weight: normal;
          }
          .top .info p {
            margin: 0 0 10px;
          }
          .top .info .bottom {
            display: flex;
            flex-flow: row nowrap;
          }
          .top .info .bottomRight {
            font-size: 13px;
            line-height: 1.3;
          }
          :global(.avatar) ~ .bottomRight {
            margin-left: 10px;
          }
          .articles {
            margin-top: 20px;
          }
          .articles h2 {
            font-size: 17px;
            margin: 30px 0 10px;
            color: #3a3a3a;
            font-weight: 600;
            letter-spacing: 0.02em;
          }
          .noResults {
            font-size: 1.3rem;
            font-weight: 400;
          }
          @media screen and (max-width: 1024px) {
            .top {
              padding-left: 80px;
            }
            .top .icon {
              font-size: 32px;
              left: 32px;
              top: 40px;
              transform: translateX(-50%);
            }
            .top .info h1 {
              font-size: 28px;
            }
          }
          @media screen and (max-width: 480px) {
            .container {
              padding: 30px 20px;
            }
            .top {
              padding-left: 20px;
            }
            .top .icon {
              position: static;
              transform: none;
              margin-bottom: 20px;
            }
            .top .info h1 {
              font-size: 24px;
            }
            .top .info .bottom :global(.avatar:not(:first-child)) {
              display: none;
            }
          }
        `}
      </style>
    </div>
  );
}

Collection.getInitialProps = async ctx => {
  const { query } = ctx;
  if (!query._id || Number.isNaN(Number(query._id))) {
    throw new ApiError('Collection not found', 404);
  }
  const collection = await fetch(`/collections/${query._id}`, {
    query: { language: query.language },
    ctx,
  }).then(resp => resp.json());
  if (!collection) {
    throw new ApiError('Collection not found', 404);
  }
  return { query, collection, namespacesRequired: i18nNs };
};

export default Collection;
